// src/components/ProfileEditModal.js — extracted from App.js
import React, { useState } from "react";
import { supabase } from "../lib/supabase";

const LABEL_STYLE = {fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",display:"block",marginBottom:5};

// ─── Profile Edit Modal ───────────────────────────────────────────────────────
export function ProfileEditModal({ session, users, sellers, saveSellers, catalogEntries, saveCatalog, onSave, toast, onClose }) {
  const u = session.user;
  const [section, setSection] = useState("profile"); // "profile" | "address" | "password"
  const [name,  setName]  = useState(u.name  || "");
  const [phone, setPhone] = useState(u.phone || "");
  const [bio,   setBio]   = useState(u.bio   || "");
  const [addr,  setAddr]  = useState({
    line1: u.addressLine1 || "",
    line2: u.addressLine2 || "",
    city:  u.addressCity  || "",
    state: u.addressState || "",
    postalCode: u.addressPostalCode || "",
    country: u.addressCountry || "India",
  });
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [saving, setSaving] = useState(false);

  const phoneChanged = phone.trim() !== (u.phone || "").trim();

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const updated = {
      ...u,
      name: name.trim(),
      phone: phone.trim(),
      bio: bio.trim(),
      addressLine1: addr.line1.trim() || null,
      addressLine2: addr.line2.trim() || null,
      addressCity:  addr.city.trim()  || null,
      addressState: addr.state.trim() || null,
      addressPostalCode: addr.postalCode.trim() || null,
      addressCountry: addr.country.trim() || "India",
    };
    if (phoneChanged) updated.phoneVerified = false;
    onSave(updated);
    if (u.sellerId) {
      await supabase.from("sellers").update({ name: name.trim() }).eq("id", u.sellerId);
      saveSellers(sellers.map(s => s.id !== u.sellerId ? s : { ...s, name: name.trim() }));
    }
    if (u.id) {
      saveCatalog((catalogEntries||[]).map(e => e.collectorId !== u.id ? e : { ...e, collectorName: name.trim() }));
    }
    setSaving(false);
    toast("Profile updated.");
    if (phoneChanged) toast("Phone changed — re-verification may be required.");
    onClose();
  };

  const handleChangePassword = async () => {
    setPwErr("");
    if (!currentPw) { setPwErr("Enter your current password."); return; }
    if (newPw.length < 6) { setPwErr("New password must be at least 6 characters."); return; }
    if (newPw !== confirmPw) { setPwErr("New passwords don't match."); return; }
    if (newPw === currentPw) { setPwErr("New password must differ from current."); return; }
    setPwSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("auth-login", {
        body: { action: "change_password", userId: u.id, currentPassword: currentPw.trim(), newPassword: newPw },
      });
      if (error || data?.error) {
        setPwErr(data?.error || "Couldn't update password — try again.");
        setPwSaving(false);
        return;
      }
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setPwSaving(false);
      toast("Password updated.");
    } catch (e) {
      setPwSaving(false);
      setPwErr("Couldn't update password — try again.");
    }
  };

  const sectionTabStyle = (active) => ({
    flex:1, padding:"8px 0", fontFamily:"'DM Mono',monospace", fontSize:10,
    letterSpacing:"0.08em", background: active?"#111118":"transparent",
    color: active?"#fff":"var(--ink3)",
    border: active?"none":"1px solid var(--bd)", borderRadius:6, cursor:"pointer",
  });

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div className="modal fade-in" style={{maxWidth:480,maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.12em",marginBottom:4}}>ACCOUNT</div>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:24,margin:0}}>My Profile</h2>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"var(--ink3)"}}>×</button>
        </div>

        <div style={{display:"flex",gap:4,marginBottom:18}}>
          <button type="button" style={sectionTabStyle(section==="profile")} onClick={()=>setSection("profile")}>PROFILE</button>
          <button type="button" style={sectionTabStyle(section==="address")} onClick={()=>setSection("address")}>ADDRESS</button>
          <button type="button" style={sectionTabStyle(section==="password")} onClick={()=>setSection("password")}>PASSWORD</button>
        </div>

        {section === "profile" && (
          <form autoComplete="on" onSubmit={e=>{e.preventDefault();handleSave();}}>
            <div style={{marginBottom:14}}>
              <label style={LABEL_STYLE}>EMAIL (READ-ONLY)</label>
              <div style={{padding:"10px 14px",background:"var(--bg2)",borderRadius:8,fontSize:13,color:"var(--ink3)",fontFamily:"'DM Mono',monospace"}}>{u.email}</div>
              <div style={{fontSize:10,color:"var(--ink3)",marginTop:4}}>To change your email, contact support — it requires re-verification.</div>
            </div>

            <div style={{marginBottom:14}}>
              <label style={LABEL_STYLE}>DISPLAY NAME</label>
              <input
                value={name} onChange={e=>setName(e.target.value)}
                placeholder="Your name" style={{fontSize:13}}
                type="text" autoComplete="name" autoCapitalize="words" required
              />
            </div>

            <div style={{marginBottom:14}}>
              <label style={LABEL_STYLE}>PHONE{phoneChanged?" · ⚠ re-verification required":""}</label>
              <input
                value={phone} onChange={e=>setPhone(e.target.value)}
                placeholder="+91 XXXXX XXXXX" style={{fontSize:13,borderColor:phoneChanged?"var(--gold)":""}}
                type="tel" inputMode="tel" autoComplete="tel"
                pattern="[+0-9 ()\-]{7,18}"
              />
              {phoneChanged && <div style={{fontSize:10,color:"var(--gold)",marginTop:4}}>Changing your phone number will require re-verification before it's used for notifications.</div>}
            </div>

            <div style={{marginBottom:18}}>
              <label style={LABEL_STYLE}>BIO <span style={{opacity:.5}}>(optional — shown on seller/collector profile)</span></label>
              <textarea
                value={bio} onChange={e=>setBio(e.target.value)}
                placeholder="A few words about you and your collection…" rows={3}
                style={{fontSize:13,resize:"vertical"}} autoCapitalize="sentences"
              />
            </div>

            <div style={{display:"flex",gap:10}}>
              <button type="submit" className="btn-ink" disabled={saving||!name.trim()} style={{flex:1,padding:"11px 0",fontSize:13}}>
                {saving?"Saving…":"Save profile"}
              </button>
              <button type="button" className="btn-ghost" onClick={onClose} style={{padding:"11px 20px",fontSize:13}}>Cancel</button>
            </div>
          </form>
        )}

        {section === "address" && (
          <form autoComplete="on" onSubmit={e=>{e.preventDefault();handleSave();}}>
            <div style={{background:"var(--bg2)",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:"var(--ink2)",lineHeight:1.55}}>
              Saved here as your default. You'll be asked to confirm or edit it when you accept an offer.
            </div>

            <div style={{marginBottom:12}}>
              <label style={LABEL_STYLE}>ADDRESS LINE 1</label>
              <input value={addr.line1} onChange={e=>setAddr(p=>({...p,line1:e.target.value}))}
                placeholder="Flat / house no., building, street" style={{fontSize:13}}
                type="text" autoComplete="address-line1" autoCapitalize="words"/>
            </div>
            <div style={{marginBottom:12}}>
              <label style={LABEL_STYLE}>ADDRESS LINE 2 <span style={{opacity:.5}}>(optional)</span></label>
              <input value={addr.line2} onChange={e=>setAddr(p=>({...p,line2:e.target.value}))}
                placeholder="Area, locality, landmark" style={{fontSize:13}}
                type="text" autoComplete="address-line2" autoCapitalize="words"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div>
                <label style={LABEL_STYLE}>CITY</label>
                <input value={addr.city} onChange={e=>setAddr(p=>({...p,city:e.target.value}))}
                  placeholder="Mumbai" style={{fontSize:13}}
                  type="text" autoComplete="address-level2" autoCapitalize="words"/>
              </div>
              <div>
                <label style={LABEL_STYLE}>STATE</label>
                <input value={addr.state} onChange={e=>setAddr(p=>({...p,state:e.target.value}))}
                  placeholder="Maharashtra" style={{fontSize:13}}
                  type="text" autoComplete="address-level1" autoCapitalize="words"/>
              </div>
              <div>
                <label style={LABEL_STYLE}>PIN CODE</label>
                <input value={addr.postalCode} onChange={e=>setAddr(p=>({...p,postalCode:e.target.value.replace(/\D/g,"").slice(0,6)}))}
                  placeholder="400001" style={{fontSize:13,fontFamily:"'DM Mono',monospace"}}
                  type="text" inputMode="numeric" autoComplete="postal-code"
                  pattern="[0-9]{6}" maxLength={6}/>
              </div>
              <div>
                <label style={LABEL_STYLE}>COUNTRY</label>
                <input value={addr.country} onChange={e=>setAddr(p=>({...p,country:e.target.value}))}
                  style={{fontSize:13}}
                  type="text" autoComplete="country-name" autoCapitalize="words"/>
              </div>
            </div>

            <div style={{display:"flex",gap:10}}>
              <button type="submit" className="btn-ink" disabled={saving} style={{flex:1,padding:"11px 0",fontSize:13}}>
                {saving?"Saving…":"Save address"}
              </button>
              <button type="button" className="btn-ghost" onClick={onClose} style={{padding:"11px 20px",fontSize:13}}>Cancel</button>
            </div>
          </form>
        )}

        {section === "password" && (
          <form autoComplete="on" onSubmit={e=>{e.preventDefault();handleChangePassword();}}>
            <div style={{background:"var(--bg2)",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:"var(--ink2)",lineHeight:1.55}}>
              {/* Email is sent to help password managers offer to update the saved entry */}
            </div>

            {/* Hidden email field so password managers know which account this is for */}
            <input type="email" value={u.email} readOnly autoComplete="username" style={{display:"none"}}/>

            <div style={{marginBottom:12}}>
              <label style={LABEL_STYLE}>CURRENT PASSWORD</label>
              <input value={currentPw} onChange={e=>{setCurrentPw(e.target.value);setPwErr("");}}
                type="password" autoComplete="current-password" placeholder="••••••••"
                style={{fontSize:13}} required/>
            </div>
            <div style={{marginBottom:12}}>
              <label style={LABEL_STYLE}>NEW PASSWORD</label>
              <input value={newPw} onChange={e=>{setNewPw(e.target.value);setPwErr("");}}
                type="password" autoComplete="new-password" placeholder="At least 6 characters"
                minLength={6} style={{fontSize:13}} required/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={LABEL_STYLE}>CONFIRM NEW PASSWORD</label>
              <input value={confirmPw} onChange={e=>{setConfirmPw(e.target.value);setPwErr("");}}
                type="password" autoComplete="new-password" placeholder="Repeat new password"
                minLength={6} style={{fontSize:13}} required/>
            </div>

            {pwErr && <div style={{color:"var(--red)",fontSize:12,marginBottom:12,background:"var(--rbg)",border:"1px solid var(--rbd)",borderRadius:6,padding:"8px 12px"}}>{pwErr}</div>}

            <div style={{display:"flex",gap:10}}>
              <button type="submit" className="btn-ink" disabled={pwSaving||!currentPw||!newPw||!confirmPw} style={{flex:1,padding:"11px 0",fontSize:13}}>
                {pwSaving?"Updating…":"Update password"}
              </button>
              <button type="button" className="btn-ghost" onClick={onClose} style={{padding:"11px 20px",fontSize:13}}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Role Capabilities ────────────────────────────────────────────────────────
